import React from 'react';
import PropTypes from 'prop-types';

import calendarIcon from './images/calendar_integration.svg';
import contactIcon from './images/contact_integration.svg';
import emailIcon from './images/buildout_email_integration.svg';
import clickToCallIcon from './images/click_to_call.svg';

import { clickAsLink } from 'helpers/link_helpers';

import ContactModal from './integration_modals/contact_modal';
import CalendarModal from './integration_modals/calendar_modal';

import IntegrationsApi from './integrations_api';
import CancelCalendarModal from './integration_modals/cancel_calendar_modal';
import CancelContactModal from './integration_modals/cancel_contact_modal';
import CancelTwilioModal from './integration_modals/cancel_twilio_modal';
import ActiveIntegrationsCard from './active_integrations_card';
import ImportCalendarModal from './integration_modals/import_calendar_modal';

export default class ActiveIntegrations extends React.Component {
  static propTypes = {
    calendarIntegration: PropTypes.shape({
      connectedCalendarId: PropTypes.number,
      databaseId: PropTypes.number,
      email: PropTypes.string
    }),
    calendarsForImport: PropTypes.array,
    clickToCallIntegration: PropTypes.shape({
      id: PropTypes.number,
      phoneNumber: PropTypes.string
    }),
    contactIntegration: PropTypes.shape({
      databaseId: PropTypes.number,
      email: PropTypes.string
    }),
    crmAccess: PropTypes.bool,
    currentDatabase: PropTypes.shape({
      forwardingEmail: PropTypes.string,
      name: PropTypes.string
    }).isRequired,
    currentUser: PropTypes.object,
    databases: PropTypes.array.isRequired,
    googleIntegration: PropTypes.shape({
      databaseId: PropTypes.number
    }),
    importCalendar: PropTypes.bool,
    paths: PropTypes.shape({
      importCalendars: PropTypes.string.isRequired,
      importContacts: PropTypes.string.isRequired,
      pushContacts: PropTypes.string.isRequired
    }).isRequired,
    pushContactsToSource: PropTypes.bool.isRequired,
    svnMember: PropTypes.bool
  };

  state = {
    showCancelCalendarModal: false,
    showCancelContactModal: false,
    showCancelTwilioModal: false,
    showEditCalendarModal: false,
    showEditContactModal: false,
    showImportCalendarModal: this.props.importCalendar
  };

  api = new IntegrationsApi();

  handleShowCancelCalendarModal = () => this.setState({ showCancelCalendarModal: true });
  handleShowCancelContactModal = () => this.setState({ showCancelContactModal: true });
  handleShowCancelTwilioModal = () => this.setState({ showCancelTwilioModal: true });
  handleShowEditCalendarModal = () => this.setState({ showEditCalendarModal: true });
  handleShowEditContactModal = () => this.setState({ showEditContactModal: true });
  handleShowImportCalendarsModal = () => this.setState({ showImportCalendarModal: true });

  handleCloseModals = () => {
    this.setState({
      showCancelCalendarModal: false,
      showCancelContactModal: false,
      showCancelTwilioModal: false,
      showEditCalendarModal: false,
      showEditContactModal: false,
      showImportCalendarModal: false
    });
  };

  handleUpdateNylasIntegration = (databaseId, scope) => {
    this.api.updateNylasIntegration(databaseId, scope, this.props.currentUser.id).then(() => {
      this.handleCloseModals();
      window.location.reload();
    });
  };

  handleUpdateGoogleIntegration = (databaseId) => {
    this.handleCloseModals();
    this.api.updateGoogleIntegration(databaseId, this.props.currentUser.id).then(() => {
      window.location.reload();
    });
  };

  handleCancelNylasIntegration = (scope) => {
    return this.api.cancelNylasIntegration(scope, this.props.currentUser.id).then(() => {
      this.handleCloseModals();
      window.location.reload();
    });
  };

  handleCancelGoogleIntegration = () => {
    this.api.cancelGoogleIntegration(this.props.currentUser.id).then(() => {
      this.handleCloseModals();
      window.location.reload();
    });
  };

  handleCancelTwilioIntegration = () => {
    const { clickToCallIntegration, currentUser } = this.props;
    this.api.cancelTwilioIntegration(clickToCallIntegration.id, currentUser.id).then(() => {
      this.handleCloseModals();
      window.location.reload();
    });
  };

  handleImportCalendars = (calendar) => {
    const { paths, currentUser } = this.props;

    this.api.handleCalendarImport(calendar, paths.importCalendars, currentUser.id).then(() => {
      this.handleCloseModals();
      window.location.reload();
    });

  };

  handleImportContacts = (e) => {
    const { paths } = this.props;
    clickAsLink(window, paths.importContacts, e);
  };

  handlePushContacts = (e) => {
    const { paths } = this.props;
    clickAsLink(window, paths.pushContacts, e);
  }

  render() {
    return (
      <div className="available-integrations">
        {this.renderActiveIntegrations()}
        {this.renderEditCalendarModal()}
        {this.renderEditContactModal()}
        {this.renderCancelCalendarModal()}
        {this.renderCancelContactModal()}
        {this.renderCancelTwilioModal()}
        {this.renderImportCalendarModal()}
      </div>
    );
  }

  renderCalendarText(noIntegration, onImport) {
    const { calendarIntegration } = this.props;

    if (noIntegration) {
      return (<span>Currently syncing calendar events from your Google Calendar</span>);
    } else if (onImport) {
      return (
        <span>Connected with <strong>"{calendarIntegration.email}". </strong>
          <span className="clickable" onClick={onImport}>
            <u>click here</u> to start syncing calendar events with Buildout.
          </span>
        </span>
      );
    } else {
      return (
        <span>Currently syncing calendar events from <strong>"{calendarIntegration.email}"</strong></span>
      );
    }
  }

  renderCalendarBlock() {
    const { calendarIntegration, currentDatabase, databases, googleIntegration } = this.props;

    const noGoogleInt = !googleIntegration || !googleIntegration.id;
    const noCalendarInt = !calendarIntegration || !calendarIntegration.id;

    if (noGoogleInt && noCalendarInt) return;

    const connectedGoogleDatabase = !noGoogleInt && databases.find(t => t.id === googleIntegration.databaseId);
    const connectedCalendarDatabase = !noCalendarInt && databases.find(t => t.id === calendarIntegration.databaseId);
    const isCurrentGoogleDatabase = connectedGoogleDatabase && googleIntegration.databaseId === currentDatabase.id;
    const isCurrentCalendarDatabase = connectedCalendarDatabase && calendarIntegration.databaseId === currentDatabase.id;

    if (!noCalendarInt && !connectedCalendarDatabase) {
      return (
        <ActiveIntegrationsCard
          image={calendarIcon}
          name="inactive-calendar"
          onCancel={this.handleShowCancelCalendarModal}
        >
          <div className="col-xs-8 pl-0">
            <h5 className="mb-1">Calendar Sync</h5>
            <div className="text-larger text-muted">
              <span>
                You currently have this integration synced with "{calendarIntegration.databaseName}"
                an inactive database. Disconnect from that database if you wish to connect to "{currentDatabase.name}"
              </span>
            </div>
          </div>
        </ActiveIntegrationsCard>
      );  
    }

    if (!isCurrentGoogleDatabase && !isCurrentCalendarDatabase) return;

    const onImport = calendarIntegration == null || calendarIntegration.connectedCalendarId != null
      ? null : this.handleShowImportCalendarsModal;

    return (
      <ActiveIntegrationsCard
        image={calendarIcon}
        name="calendar"
        onCancel={this.handleShowCancelCalendarModal}
        onEdit={this.handleShowEditCalendarModal}
        onImport={onImport}
      >
        <div className="col-xs-8 pl-0">
          <h5 className="mb-1">Calendar Sync</h5>
          <div className="text-larger text-muted">
            {this.renderCalendarText(noCalendarInt, onImport)}
          </div>
        </div>
      </ActiveIntegrationsCard>
    );
  }

  renderContactBlock() {
    const { contactIntegration, currentDatabase, databases, pushContactsToSource } = this.props;

    if (!contactIntegration) return;

    const connectedDatabase = databases.find(t => t.id === contactIntegration.databaseId);

    if (!connectedDatabase) {
      return (
        <ActiveIntegrationsCard
          image={contactIcon}
          name="inactive-contacts"
          onCancel={this.handleShowCancelContactModal}
        >
          <div className="col-xs-8 pl-0">
            <h5 className="mb-1">Contact Sync</h5>
            <div className="text-larger text-muted">
              <span>
                You currently have this integration synced with "{contactIntegration.databaseName}"
                an inactive database. Disconnect from that database if you wish to connect to "{currentDatabase.name}"
              </span>
            </div>            
          </div>
        </ActiveIntegrationsCard>
      );  
    }

    if (contactIntegration.databaseId !== currentDatabase.id) return;

    return (
      <ActiveIntegrationsCard
        image={contactIcon}
        name="contacts"
        pushContactsToSource={pushContactsToSource}
        onCancel={this.handleShowCancelContactModal}
        onEdit={this.handleShowEditContactModal}
        onImport={this.handleImportContacts}
        onPush={this.handlePushContacts}
      >
        <div className="col-xs-8 pl-0">
          <h5 className="mb-1">Contact Sync</h5>
        </div>
      </ActiveIntegrationsCard>
    );
  }

  renderClickToCallBlock() {
    const { clickToCallIntegration } = this.props;

    if (clickToCallIntegration == null) return;

    return (
      <ActiveIntegrationsCard image={clickToCallIcon} name="click-to-call" onCancel={this.handleShowCancelTwilioModal}>
        <div className="col-xs-8 pl-0">
          <h5 className="mb-1">Click-to-Call</h5>
          <div className="text-larger text-muted">
            Currently connected to <strong>"{clickToCallIntegration.phoneNumber}"</strong>
          </div>
        </div>
      </ActiveIntegrationsCard>
    );
  }

  renderHelpCenterLink() {
    const { crmAccess, svnMember } = this.props;
    if (!crmAccess || !svnMember) return null;

    return (
      <a
        className="clickable"
        name="help-center-link"
        rel="noopener noreferrer"
        target="_blank"
      >
        How to log an email automatically from your email client
      </a>
    );
  }

  renderEmailBlock() {
    const { currentDatabase } = this.props;
    return (
      <ActiveIntegrationsCard image={emailIcon} name="email">
        <div className="col-xs-8 pl-0">
          <h5 className="mb-1">Buildout Database Email Forwarding</h5>
          { this.renderHelpCenterLink() }
          <div className="pt-4">"{currentDatabase.name}" Forwarding Email: </div>
          <div className="text-larger">{currentDatabase.forwardingEmail}</div>
        </div>
      </ActiveIntegrationsCard>
    );
  }

  renderEditContactModal() {
    const { showEditContactModal } = this.state;
    const { currentDatabase, databases } = this.props;

    return (
      <ContactModal
        currentDatabase={currentDatabase}
        databases={databases}
        shown={showEditContactModal}
        onCancel={this.handleCloseModals}
        onSave={this.handleUpdateNylasIntegration}
      />
    );
  }

  renderEditCalendarModal() {
    const { showEditCalendarModal } = this.state;
    const { calendarIntegration, currentDatabase, databases } = this.props;


    const onSave = calendarIntegration == null ?
      this.handleUpdateGoogleIntegration : this.handleUpdateNylasIntegration;

    return (
      <CalendarModal
        currentDatabase={currentDatabase}
        databases={databases}
        shown={showEditCalendarModal}
        onCancel={this.handleCloseModals}
        onSave={onSave}
      />
    );
  }

  renderCancelCalendarModal() {
    const { calendarIntegration } = this.props;
    const { showCancelCalendarModal } = this.state;

    const onSave = calendarIntegration == null
      ? this.handleCancelGoogleIntegration : this.handleCancelNylasIntegration;

    return (
      <CancelCalendarModal
        shown={showCancelCalendarModal}
        onCancel={this.handleCloseModals}
        onSave={onSave}
      />
    );
  }

  renderCancelTwilioModal() {
    const { showCancelTwilioModal } = this.state;

    return (
      <CancelTwilioModal
        shown={showCancelTwilioModal}
        onCancel={this.handleCloseModals}
        onSave={this.handleCancelTwilioIntegration}
      />
    );
  }

  renderImportCalendarModal() {
    const { showImportCalendarModal } = this.state;
    const { calendarsForImport, currentDatabase } = this.props;

    return (
      <ImportCalendarModal
        calendars={calendarsForImport}
        currentDatabase={currentDatabase}
        shown={showImportCalendarModal}
        onCancel={this.handleCloseModals}
        onSave={this.handleImportCalendars}
      />
    );
  }

  renderCancelContactModal() {
    const { showCancelContactModal } = this.state;

    return (
      <CancelContactModal
        shown={showCancelContactModal}
        onCancel={this.handleCloseModals}
        onSave={this.handleCancelNylasIntegration}
      />
    );
  }

  renderActiveIntegrations() {
    return (
      <React.Fragment>
        <h4 className="active-title">Active Integrations</h4>
        {this.renderEmailBlock()}
        {this.renderCalendarBlock()}
        {this.renderContactBlock()}
        {this.renderClickToCallBlock()}
      </React.Fragment>
    );
  }
}
